import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  AuthenticationExtensionsClientOutputs,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import * as api from './api';
import { base64urlToBuffer } from './base64url';
import { generateMasterKey, prfEvalFirst, unwrapMasterKey, wrapMasterKey } from './crypto';
import { getDeviceId, getMasterKey, setMasterKey, setStoredSession, setTrustWindowDays } from './session';

/**
 * `@simplewebauthn/browser`'s types don't model the PRF extension (it predates the spec landing
 * in browsers), but it spreads `optionsJSON` straight into the options passed to
 * `navigator.credentials.create()/get()` and returns `credential.getClientExtensionResults()`
 * verbatim — so requesting/reading `prf` works at runtime even though it isn't typed. Hence the
 * `as` casts below: they widen to what the browser actually supports, not what the library types.
 */
type ExtendedCreationOptions = PublicKeyCredentialCreationOptionsJSON & {
  extensions?: PublicKeyCredentialCreationOptionsJSON['extensions'] & { prf?: Record<string, never> };
};
type ExtendedRequestOptions = PublicKeyCredentialRequestOptionsJSON & {
  extensions?: PublicKeyCredentialRequestOptionsJSON['extensions'] & { prf?: { eval?: { first: ArrayBuffer } } };
};
type ExtendedClientExtensionResults = AuthenticationExtensionsClientOutputs & {
  prf?: { results?: { first?: ArrayBuffer } };
};

function bufferToBase64urlLocal(buf: ArrayBuffer): string {
  let binary = '';
  for (const byte of new Uint8Array(buf)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Registers a passkey, asking for the PRF extension first. Some browsers (Firefox, as of this
 * writing) reject `navigator.credentials.create()` outright the moment `prf` appears in
 * `extensions` at all — no authenticator UI even shows. When that happens, we fetch a fresh
 * challenge (the first one is left to expire unused) and retry with no `prf` key present, so
 * registration still succeeds; the caller just won't get a usable master-key wrap for it.
 */
async function registerWithOptionalPrf(
  fetchOptions: () => Promise<{ options: PublicKeyCredentialCreationOptionsJSON; challengeId: string }>,
): Promise<{
  response: Awaited<ReturnType<typeof startRegistration>>;
  challengeId: string;
  prfRequested: boolean;
}> {
  const first = await fetchOptions();
  try {
    const withPrf: ExtendedCreationOptions = { ...first.options, extensions: { ...first.options.extensions, prf: {} } };
    const response = await startRegistration({ optionsJSON: withPrf });
    return { response, challengeId: first.challengeId, prfRequested: true };
  } catch {
    const second = await fetchOptions();
    const response = await startRegistration({ optionsJSON: second.options });
    return { response, challengeId: second.challengeId, prfRequested: false };
  }
}

/** Same fallback pattern as {@link registerWithOptionalPrf}, for the login (authentication) ceremony. */
async function authenticateWithOptionalPrf(
  fetchOptions: () => Promise<{ options: PublicKeyCredentialRequestOptionsJSON; challengeId: string }>,
): Promise<{ response: Awaited<ReturnType<typeof startAuthentication>>; challengeId: string }> {
  const salt = await prfEvalFirst();
  const first = await fetchOptions();
  try {
    const withPrf: ExtendedRequestOptions = {
      ...first.options,
      extensions: { ...first.options.extensions, prf: { eval: { first: salt } } },
    };
    const response = await startAuthentication({ optionsJSON: withPrf });
    return { response, challengeId: first.challengeId };
  } catch {
    const second = await fetchOptions();
    const response = await startAuthentication({ optionsJSON: second.options });
    return { response, challengeId: second.challengeId };
  }
}

/**
 * Runs a local, throwaway `navigator.credentials.get()` ceremony against `credentialId` purely
 * to evaluate the PRF extension — not verified by (or sent to) the server. Needed because some
 * browsers only report PRF *availability* during `create()`, not the actual secret; a follow-up
 * `get()` on the same credential reliably returns it.
 */
async function extractPrfOutput(credentialId: string): Promise<ArrayBuffer> {
  const salt = await prfEvalFirst();
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      allowCredentials: [{ id: base64urlToBuffer(credentialId), type: 'public-key' }],
      userVerification: 'required',
      extensions: { prf: { eval: { first: salt } } },
    },
  } as unknown as CredentialRequestOptions)) as PublicKeyCredential | null;

  if (!credential) throw new Error('Passkey verification was not completed');
  const results = credential.getClientExtensionResults() as ExtendedClientExtensionResults;
  const prfOutput = results.prf?.results?.first;
  if (!prfOutput) throw new Error('PRF extension was enabled but returned no usable output');
  return prfOutput;
}

export interface SignUpResult {
  userId: string;
  trustWindowDays: number;
  /**
   * False when this browser/authenticator doesn't support the WebAuthn PRF extension. The
   * account and passkey still work fine for signing in — this only means this particular
   * passkey can't unlock synced API-key encryption (add a PRF-capable passkey later to enable it).
   */
  prfSupported: boolean;
}

/** New-account signup: register a passkey, and — if PRF is supported — generate + wrap master key K. */
export async function signUp(email: string): Promise<SignUpResult> {
  const deviceId = getDeviceId();
  const { response, challengeId, prfRequested } = await registerWithOptionalPrf(() => api.registerOptions(email));

  let wrappedMasterKey: string | undefined;
  let wrapIv: string | undefined;
  let masterKey: CryptoKey | null = null;
  let prfSupported = false;

  if (prfRequested) {
    try {
      const prfOutput = await extractPrfOutput(response.id);
      masterKey = await generateMasterKey();
      ({ wrappedMasterKey, wrapIv } = await wrapMasterKey(masterKey, prfOutput));
      prfSupported = true;
    } catch {
      // Registration succeeded but the follow-up PRF-eval ceremony didn't pan out — proceed
      // without a wrapped key rather than fail the whole signup over a sync-only feature.
    }
  }

  const result = await api.registerVerify({
    challengeId,
    email,
    deviceId,
    response,
    wrappedMasterKey,
    wrapIv,
    label: navigator.userAgent.slice(0, 120),
  });

  setStoredSession({
    sessionToken: result.sessionToken,
    userId: result.userId,
    email,
    trustWindowDays: result.trustWindowDays,
    deviceTrustToken: result.deviceTrustToken,
  });
  setTrustWindowDays(result.trustWindowDays);
  setMasterKey(masterKey);

  return { userId: result.userId, trustWindowDays: result.trustWindowDays, prfSupported };
}

export interface SignInResult {
  userId: string;
  trustWindowDays: number;
}

/** Passkey login — always (re)establishes device trust; unwraps K via the credential just used if possible. */
export async function signInWithPasskey(email?: string): Promise<SignInResult> {
  const deviceId = getDeviceId();
  const { response, challengeId } = await authenticateWithOptionalPrf(() => api.loginOptions(email));

  const result = await api.loginVerify({
    challengeId,
    deviceId,
    response,
    label: navigator.userAgent.slice(0, 120),
  });

  setStoredSession({
    sessionToken: result.sessionToken,
    userId: result.userId,
    email: email ?? '',
    trustWindowDays: result.trustWindowDays,
    deviceTrustToken: result.deviceTrustToken,
  });
  setTrustWindowDays(result.trustWindowDays);

  const extResults = response.clientExtensionResults as ExtendedClientExtensionResults;
  const prfOutput = extResults.prf?.results?.first;
  if (prfOutput && result.wrappedKey) {
    try {
      const masterKey = await unwrapMasterKey(
        { wrappedMasterKey: result.wrappedKey.wrappedMasterKey, wrapIv: result.wrappedKey.wrapIv },
        prfOutput,
      );
      setMasterKey(masterKey);
    } catch {
      // Wrong/rotated key material — sync features stay locked until the user retries or re-adds a passkey.
      setMasterKey(null);
    }
  }

  return { userId: result.userId, trustWindowDays: result.trustWindowDays };
}

/** Adds an additional passkey to the already-signed-in user, re-wrapping the same master key K for it. */
export async function addPasskey(): Promise<{ prfSupported: boolean }> {
  const masterKey = getMasterKey();
  if (!masterKey) {
    throw new Error('Unlock your synced key with an existing passkey first, then add a new one.');
  }
  const { response, challengeId, prfRequested } = await registerWithOptionalPrf(() => api.addPasskeyOptions());

  let wrappedMasterKey: string | undefined;
  let wrapIv: string | undefined;
  let prfSupported = false;

  if (prfRequested) {
    try {
      const prfOutput = await extractPrfOutput(response.id);
      ({ wrappedMasterKey, wrapIv } = await wrapMasterKey(masterKey, prfOutput));
      prfSupported = true;
    } catch {
      // This passkey will still work for signing in — it just can't unlock synced encryption.
    }
  }

  await api.addPasskeyVerify({
    challengeId,
    response,
    wrappedMasterKey,
    wrapIv,
    label: navigator.userAgent.slice(0, 120),
  });

  return { prfSupported };
}

/** Re-derives K from any of the user's registered passkeys — used when K isn't in memory (e.g. after reload). */
export async function unlockMasterKey(): Promise<void> {
  const { wrappedKeys } = await api.getWrappedKeys();
  if (wrappedKeys.length === 0) {
    throw new Error(
      'None of your passkeys support secure sync (the PRF extension). Add a passkey on a browser/device that supports it — most security keys and Chrome/Edge do.',
    );
  }

  const salt = await prfEvalFirst();
  const credentialIds = wrappedKeys.map((k) => base64urlToBuffer(k.credentialId));
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      allowCredentials: credentialIds.map((id) => ({ id, type: 'public-key' })),
      userVerification: 'required',
      extensions: { prf: { eval: { first: salt } } },
    },
  } as unknown as CredentialRequestOptions)) as PublicKeyCredential | null;

  if (!credential) throw new Error('Passkey verification was not completed');
  const results = credential.getClientExtensionResults() as ExtendedClientExtensionResults;
  const prfOutput = results.prf?.results?.first;
  if (!prfOutput) throw new Error('This authenticator does not support the PRF extension');

  const usedId = bufferToBase64urlLocal(credential.rawId);
  const envelope = wrappedKeys.find((k) => k.credentialId === usedId) ?? wrappedKeys[0];
  const masterKey = await unwrapMasterKey(
    { wrappedMasterKey: envelope.wrappedMasterKey, wrapIv: envelope.wrapIv },
    prfOutput,
  );
  setMasterKey(masterKey);
}
